import React, { useEffect, useState } from 'react';
import { useKato } from '../../../../contexts/kato.context';
import {
  EmploymentCategories,
  IHumanResources,
  IScreeningPopulationByAge,
  IScreeningPopulationDynamic,
  SocialCategories,
} from '../../../../types/screening.types';
import classes from '../styles.module.scss';
import { useShared } from '../../../../contexts/shared.context';
import { isEmpty, t } from '../../../../helpers/utils';
import { useTranslation } from 'react-i18next';
import Field from '../../../../components/field/field.component';
import { useParams } from 'react-router-dom';
import FooterComponent from '../../components/footer.component';
import { updateScreening } from '../../../../requests/screening.request';
import { toast } from 'react-toastify';

const baseClass = classes['screening-tab__human-resources'];
const translationKey = 'screening.human-resources';

type ArrayKeys = 'populationDynamic' | 'populationByAge';
type ObjectKeys = 'employment' | 'populationBySocialStatus';

const isArrayKey = (key: keyof IHumanResources): key is ArrayKeys =>
  key === 'populationByAge' || key === 'populationDynamic';

const isObjectKey = (key: keyof IHumanResources): key is ObjectKeys =>
  key === 'employment' || key === 'populationBySocialStatus';

const labelAndKey: {
  label: string;
  key: keyof IHumanResources;
}[] = [
  {
    key: 'employment',
    label: 'employment',
  },
  {
    key: 'populationByAge',
    label: 'population-by-age',
  },
  {
    key: 'populationDynamic',
    label: 'population-dynamic',
  },
  {
    key: 'populationBySocialStatus',
    label: 'population-by-social-status',
  },
];

const HumanResourcesTab = () => {
  const { selectedInfoYear } = useShared();

  const defaultHumanResources: IHumanResources = {
    employment: {
      [EmploymentCategories.hired_budget]: 0,
      [EmploymentCategories.hired_private]: 0,
      [EmploymentCategories.selfEmployed]: 0,
      [EmploymentCategories.unemployed]: 0,
      [EmploymentCategories.workingElderly]: 0,
    },
    populationByAge: [
      { age: '0-5', population: 0 },
      { age: '6-17', population: 0 },
      { age: '18-29', population: 0 },
      { age: '30-62', population: 0 },
      { age: '63 и старше', population: 0 },
    ],
    populationDynamic: [
      { year: selectedInfoYear - 5, population: 0 },
      { year: selectedInfoYear - 4, population: 0 },
      { year: selectedInfoYear - 3, population: 0 },
      { year: selectedInfoYear - 2, population: 0 },
      { year: selectedInfoYear - 1, population: 0 },
      { year: selectedInfoYear, population: 0 },
    ],
    populationBySocialStatus: {
      [SocialCategories.TSArecipients]: 0,
      [SocialCategories.firstGroupDisabled]: 0,
      [SocialCategories.pensioners]: 0,
      [SocialCategories.secondGroupDisabled]: 0,
      [SocialCategories.thirdGroupDisabled]: 0,
      [SocialCategories.largeFamilies]: 0,
    },
  };
  const { kato } = useParams<{ kato: string }>();
  const {
    i18n: { language },
  } = useTranslation();
  const {
    screeningInfo: { humanResources },
    updateScreeningInfo,
  } = useKato();
  const [formData, setFormData] = useState<IHumanResources>(
    defaultHumanResources,
  );
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const getDisabledCount = () => {
    if (!formData.populationBySocialStatus) return 0;
    const {
      [SocialCategories.firstGroupDisabled]: f,
      [SocialCategories.secondGroupDisabled]: s,
      [SocialCategories.thirdGroupDisabled]: t,
    } = formData.populationBySocialStatus;
    return Number(f) + Number(s) + Number(t);
  };

  const handleSave = () => {
    if (validateFields() && kato) {
      updateScreening(+kato, 'humanResources', formData, selectedInfoYear)
        .then(() => {
          updateScreeningInfo();
          toast.success(t(`admin.success`, language));
        })
        .catch((error) => {
          console.error('Error updating human resources:', error);
        });
    }
  };
  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};

    (Object.keys(formData) as (keyof IHumanResources)[]).forEach((key) => {
      const value = formData[key];

      if (isArrayKey(key) && Array.isArray(value)) {
        const hasAnyFilled = value.some((el) => isEmpty(el.population));
        if (hasAnyFilled) {
          value.forEach((el, index) => {
            if (isEmpty(el.population)) {
              newErrors[`${key}-${index}`] = true;
            }
          });
        }
      }

      if (isObjectKey(key) && typeof value === 'object' && value !== null) {
        const entries = Object.entries(value);
        const hasAnyFilled = entries.some(([, v]) => isEmpty(v));
        if (hasAnyFilled) {
          entries.forEach(([subKey, v]) => {
            if (isEmpty(v)) {
              newErrors[`${key}-${subKey}`] = true;
            }
          });
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (humanResources) {
      setFormData(humanResources);
    } else {
      setFormData(defaultHumanResources);
    }
  }, [humanResources]);

  if (!humanResources || !kato) return null;

  const renderFields = (lang: 'ru' | 'kz') => {
    return (
      <div className={classes['fields-container']}>
        {labelAndKey.map(({ key, label }) => {
          return (
            <div className={classes[`field-block`]} key={key}>
              <div className={classes['field-block__title']}>
                {t(`${translationKey}.${label}`, lang)}
              </div>
              <div className={classes['field-block__fields']}>
                {isArrayKey(key) ? (
                  formData[key]?.map((item, index) => {
                    const value = item.population ?? '';
                    const label =
                      key === 'populationByAge'
                        ? (item as IScreeningPopulationByAge).age
                        : (item as IScreeningPopulationDynamic).year.toString();

                    return (
                      <Field
                        key={`${key}-${index}`}
                        value={value}
                        onChange={(newValue) => {
                          const val = newValue === '' ? '' : +newValue;
                          const updatedArray = formData[key]?.map((el) => {
                            if (key === 'populationByAge') {
                              const elTyped = el as IScreeningPopulationByAge;
                              const itemTyped =
                                item as IScreeningPopulationByAge;
                              return {
                                ...elTyped,
                                population:
                                  elTyped.age === itemTyped.age
                                    ? val
                                    : elTyped.population,
                              };
                            } else {
                              const elTyped = el as IScreeningPopulationDynamic;
                              const itemTyped =
                                item as IScreeningPopulationDynamic;
                              return {
                                ...elTyped,
                                population:
                                  elTyped.year === itemTyped.year
                                    ? val
                                    : elTyped.population,
                              };
                            }
                          });
                          setFormData({ ...formData, [key]: updatedArray });
                        }}
                        label={label}
                        required
                        type="number"
                        error={errors[`${key}-${index}`]}
                      />
                    );
                  })
                ) : isObjectKey(key) ? (
                  <>
                    {formData[key] &&
                      Object.entries(formData[key]).map(([subKey, value]) => (
                        <Field
                          key={subKey}
                          value={value}
                          onChange={(newValue) => {
                            const val = newValue === '' ? '' : +newValue;
                            setFormData({
                              ...formData,
                              [key]: {
                                ...formData[key],
                                [subKey]: val,
                              },
                            });
                          }}
                          label={t(`${translationKey}.${subKey}`, lang)}
                          required
                          type="number"
                          error={errors[`${key}-${subKey}`]}
                        />
                      ))}

                    {key === 'populationBySocialStatus' && (
                      <Field
                        key={'disabled'}
                        value={getDisabledCount()}
                        onChange={(newValue) => {}}
                        label={t(`${translationKey}.disabled`, lang)}
                        type="number"
                        disabled
                      />
                    )}
                  </>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={classes[baseClass]}>
      <div className={classes[`grid`]}>
        {renderFields(language as 'ru' | 'kz')}
        {renderFields(language === 'ru' ? 'kz' : 'ru')}
      </div>

      <FooterComponent onSave={handleSave} validateFields={validateFields} />
    </div>
  );
};

export default HumanResourcesTab;
